import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Collapse,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AppleIcon from "@mui/icons-material/Apple";
import WindowIcon from "@mui/icons-material/Window";
import StorageIcon from "@mui/icons-material/Storage";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { CREATE_ENROLLMENT_TOKEN } from "../../apis/settingsApis/assets-apis/assetsApis";
import {
  getAgentDownloadSignedUrl,
  AGENT_BINARY_PATHS,
  AGENT_FILE_NAMES,
} from "../../apis/settingsApis/policy-apis/policyApis";
import WindowsSmartScreenNotice from "./WindowsSmartScreenNotice";

const AGENT_DISCLAIMER = [
  "Runs in the background to help monitor device security compliance",
  "Reviews relevant system security settings — such as encryption, firewall, antivirus, and related protections",
  "Shares device health information with your Regodit dashboard",
  "Designed not to access personal files, documents, conversations, or similar personal content",
];

const OS_BUTTONS = [
  { key: "macos", label: "macOS", Icon: AppleIcon, variants: [
    { key: "macos_silicon", label: "Apple Silicon" },
    { key: "macos_intel",   label: "Intel" },
  ]},
  { key: "windows", label: "Windows", Icon: WindowIcon },
  { key: "linux",   label: "Linux",   Icon: StorageIcon },
];

const INSTALL_STEPS = [
  "Download the installer for your operating system above.",
  "Generate an enrollment token below and paste it when prompted during setup.",
  "Once enrolled, your device appears on this page and starts reporting its security status.",
];

// macOS uninstall — run each command in Terminal, one at a time.
const MACOS_UNINSTALL_STEPS = [
  {
    label: "Quit the running agent",
    cmd: `osascript -e 'quit app "Rego WatchTower"' 2>/dev/null; pkill -f "Rego WatchTower"`,
  },
  {
    label: "Remove the app (you'll be asked for your password)",
    cmd: `sudo rm -rf "/Applications/Rego WatchTower.app"`,
  },
  {
    label: "Remove app data, logs, caches, preferences and saved state",
    cmd: `rm -rf ~/Library/Application\\ Support/Rego\\ WatchTower \\
       ~/Library/Application\\ Support/rego-watch-tower \\
       ~/Library/Logs/Rego\\ WatchTower \\
       ~/Library/Logs/rego-watch-tower \\
       ~/Library/Caches/com.regodit.watchtower \\
       ~/Library/Preferences/com.regodit.watchtower.plist \\
       ~/Library/Saved\\ Application\\ State/com.regodit.watchtower.savedState \\
       ~/Library/HTTPStorages/com.regodit.watchtower`,
  },
  {
    label: "Remove the login item",
    cmd: `osascript -e 'tell application "System Events" to delete login item "Rego WatchTower"' 2>/dev/null`,
  },
];

const EnrollDeviceDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [minted, setMinted] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [downloadErrors, setDownloadErrors] = useState({});
  const [macosMenuAnchor, setMacosMenuAnchor] = useState(null);
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState(null);

  const primary = theme.customColors.button.primary;
  const primaryHover = theme.palette._blue?.dark || theme.customColors.button.primary;

  const [createEnrollmentToken, { loading: tokenLoading, error: tokenError }] =
    useMutation(CREATE_ENROLLMENT_TOKEN);

  const handleGenerate = async () => {
    setCopied(false);
    const { data } = await createEnrollmentToken();
    if (data?.createEnrollmentToken?.success) {
      setMinted(data.createEnrollmentToken.data);
    }
  };

  const handleCopy = async () => {
    if (!minted?.token) return;
    await navigator.clipboard.writeText(minted.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleCopyCmd = async (text, idx) => {
    await navigator.clipboard.writeText(text);
    setCopiedCmd(idx);
    setTimeout(() => setCopiedCmd((cur) => (cur === idx ? null : cur)), 1800);
  };

  const handleDownload = async (osKey) => {
    setDownloading(osKey);
    setDownloadErrors((prev) => ({ ...prev, [osKey]: "" }));
    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims())?.__raw;
      const filePath = AGENT_BINARY_PATHS[osKey];
      const fileName = AGENT_FILE_NAMES[osKey];

      const { url: signedUrl } = await getAgentDownloadSignedUrl(
        filePath,
        fileName,
        accessToken,
        idToken
      );

      try {
        const res = await fetch(signedUrl, { mode: "cors" });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setDownloadErrors((prev) => ({
        ...prev,
        [osKey]: err?.message || "Download failed. Please try again.",
      }));
    } finally {
      setDownloading(null);
    }
  };

  const handleClose = () => {
    setMinted(null);
    setCopied(false);
    setDownloadErrors({});
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.1rem",
          color: primary,
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        Connect Device
        <IconButton onClick={handleClose} sx={{ color: theme.customColors.text.secondary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: theme.customColors.background.cardHeader }}>
        <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
          If your organization uses <strong>Rego WatchTower</strong> to monitor device
          security. Follow the steps below to enroll this device.
        </Alert>

        {/* Download Buttons */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, color: theme.customColors.text.strong, fontSize: "0.9375rem", mb: 1.5 }}>
            Step 1 - Download the agent
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {OS_BUTTONS.map(({ key, label, Icon, variants }) => {
              const isLoading = variants
                ? variants.some((v) => downloading === v.key)
                : downloading === key;

              return (
                <Box key={key} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Button
                    variant="contained"
                    disabled={!!downloading}
                    onClick={(e) => {
                      if (variants) setMacosMenuAnchor(e.currentTarget);
                      else handleDownload(key);
                    }}
                    startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <Icon sx={{ fontSize: 17 }} />}
                    endIcon={variants && !isLoading ? <ArrowDropDownIcon sx={{ fontSize: 17 }} /> : null}
                    sx={{
                      backgroundColor: primary,
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      px: 2.5,
                      py: 1,
                      borderRadius: "8px",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: primaryHover, boxShadow: "none" },
                      "&:disabled": { backgroundColor: theme.customColors.background.light, color: theme.customColors.text.disabled },
                    }}
                  >
                    {isLoading ? "Downloading…" : label}
                  </Button>
                  {(downloadErrors[key] || variants?.some((v) => downloadErrors[v.key])) && (
                    <Typography variant="caption" color="error">
                      {downloadErrors[key] || variants?.map((v) => downloadErrors[v.key]).find(Boolean)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* macOS chip variant menu */}
          <Menu
            anchorEl={macosMenuAnchor}
            open={Boolean(macosMenuAnchor)}
            onClose={() => setMacosMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 0.75,
                borderRadius: "12px",
                border: `1px solid ${theme.customColors.border.subtle}`,
                minWidth: 260,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
                p: 0,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.customColors.border.main}`, backgroundColor: theme.customColors.background.cardHeader }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: theme.customColors.text.secondary, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.68rem" }}>
                Select your Mac chip
              </Typography>
            </Box>
            {OS_BUTTONS.find((b) => b.key === "macos").variants.map(({ key, label }, i, arr) => (
              <MenuItem
                key={key}
                onClick={() => { setMacosMenuAnchor(null); handleDownload(key); }}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: i < arr.length - 1 ? `1px solid ${theme.customColors.border.main}` : "none",
                  "&:hover": { backgroundColor: alpha(primary, 0.06) },
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: alpha(primary, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AppleIcon sx={{ fontSize: 20, color: primary }} />
                </Box>
                <Typography variant="body2" fontWeight={700} color={theme.customColors.text.strong}>
                  {label}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Positive privacy / purpose disclaimer */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "10px",
            border: `1px solid ${theme.customColors.border.subtle}`,
            backgroundColor: theme.customColors.background.main,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: theme.customColors.text.strong, mb: 1, fontSize: "0.875rem" }}
          >
            About Rego WatchTower
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {AGENT_DISCLAIMER.map((line, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: primary,
                    mt: 0.85,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.8125rem",
                    color: theme.customColors.text.secondary,
                    lineHeight: 1.55,
                  }}
                >
                  {line}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Installation Steps */}
        <Box
          sx={{
            backgroundColor: theme.customColors.background.main,
            border: `1px solid ${theme.customColors.border.subtle}`,
            borderRadius: 2,
            p: 2.5,
            mb: 3,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: theme.customColors.text.strong, mb: 1.5, fontSize: "0.9375rem" }}>
            Step 2 - Install &amp; enroll
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {INSTALL_STEPS.map((step, i) => (
              <Box key={i} sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
                <ArrowRightIcon sx={{ fontSize: 20, color: primary, flexShrink: 0, mt: 0.15 }} />
                <Typography variant="body2" sx={{ color: theme.customColors.text.secondary, lineHeight: 1.6 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* First Windows release has no SmartScreen reputation yet — set expectations here,
              at the moment the user actually runs the installer. */}
          <WindowsSmartScreenNotice />
        </Box>

        {/* Enrollment Token */}
        <Box
          sx={{
            backgroundColor: theme.customColors.background.main,
            border: `1px solid ${theme.customColors.border.subtle}`,
            borderRadius: 2,
            p: 2.5,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: theme.customColors.text.strong, mb: 0.75, fontSize: "0.9375rem" }}>
            Step 3 - Generate enrollment token
          </Typography>
          <Typography variant="body2" sx={{ color: theme.customColors.text.secondary, mb: 1.5 }}>
            Paste this token during setup to enroll your device. Valid for 24 hours.
          </Typography>

          {!minted ? (
            <>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={tokenLoading}
                sx={{
                  backgroundColor: primary,
                  "&:hover": { backgroundColor: primaryHover },
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {tokenLoading ? <CircularProgress size={18} sx={{ color: theme.customColors.button.text }} /> : "Generate Token"}
              </Button>
              {tokenError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {tokenError.message}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Box
                sx={{
                  position: "relative",
                  p: 2,
                  pr: 6,
                  backgroundColor: theme.customColors.background.cardHeader,
                  border: `1px solid ${theme.customColors.border.subtle}`,
                  borderRadius: 2,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: "12px",
                  color: theme.customColors.text.strong,
                  wordBreak: "break-all",
                }}
              >
                {minted.token}
                <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
                  <IconButton
                    onClick={handleCopy}
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8, color: theme.customColors.icons.primary }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" sx={{ color: theme.customColors.text.secondary, display: "block", mt: 1 }}>
                Expires {new Date(minted.expiresAt).toLocaleString()} · Lost or expired? Generate a new one.
              </Typography>

              {/* Ownership is fixed by whoever registers with this token and cannot
                  be reassigned afterwards, so it has to be said before the token is
                  used — not after, when it is too late to hand it to someone else. */}
              
            </>
          )}
        </Box>

        {/* Uninstall the agent (macOS) — revealed via the info toggle */}
        <Box
          sx={{
            mt: 3,
            border: `1px solid ${theme.customColors.border.subtle}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setUninstallOpen((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 1.75,
              cursor: "pointer",
              backgroundColor: theme.customColors.background.main,
              "&:hover": { backgroundColor: theme.customColors.background.cardHeader },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18, color: theme.customColors.text.secondary }} />
            <Typography sx={{ fontWeight: 600, color: theme.customColors.text.strong, fontSize: "0.9375rem", flex: 1 }}>
              Uninstall the agent (macOS)
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.customColors.text.secondary }} />
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: theme.customColors.text.secondary,
                transform: uninstallOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </Box>

          <Collapse in={uninstallOpen}>
            <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${theme.customColors.border.subtle}` }}>
              <Typography variant="body2" sx={{ color: theme.customColors.text.secondary, mb: 2, lineHeight: 1.6 }}>
                Open the <strong>Terminal</strong> app (Applications → Utilities → Terminal),
                then copy and paste each command below one at a time, pressing <strong>Return</strong> after each.
              </Typography>

              {MACOS_UNINSTALL_STEPS.map((step, i) => (
                <Box key={i} sx={{ mb: i < MACOS_UNINSTALL_STEPS.length - 1 ? 2 : 0 }}>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: theme.customColors.text.strong, mb: 0.5 }}>
                    {i + 1}. {step.label}
                  </Typography>
                  <Box
                    sx={{
                      position: "relative",
                      pr: 5,
                      p: 1.5,
                      backgroundColor: theme.customColors.background.cardHeader,
                      border: `1px solid ${theme.customColors.border.subtle}`,
                      borderRadius: 1.5,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                      fontSize: "12px",
                      color: theme.customColors.text.strong,
                      whiteSpace: "pre",
                      overflowX: "auto",
                    }}
                  >
                    {step.cmd}
                    <Tooltip title={copiedCmd === i ? "Copied!" : "Copy"} arrow>
                      <IconButton
                        onClick={() => handleCopyCmd(step.cmd, i)}
                        size="small"
                        sx={{ position: "absolute", top: 4, right: 4, color: theme.customColors.icons.primary }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: primary,
            "&:hover": { backgroundColor: primaryHover },
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          {minted ? "Done" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnrollDeviceDialog;
